import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Download, Clock, CheckCircle, ArrowLeft, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const AffidavitsDeclarations = () => {
  const [step2Open, setStep2Open] = useState(false);
  const [step3Open, setStep3Open] = useState(false);
  const [step4Open, setStep4Open] = useState(false);
  

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/diy-processes">
            <Button 
              variant="ghost" 
              className="mb-8 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to DIY Processes
            </Button>
          </Link>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-xl bg-primary/10">
                <FileText className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Author Declarations, Affidavits, Notifications, and Recordings
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Understand the importance of making statements from the deepest part of your soul, 
              and then letting everyone know whom you have declared yourself to be.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Time Estimate</h3>
              <p className="text-muted-foreground">24 hours</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Download className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Templates</h3>
              <p className="text-muted-foreground">2 templates included</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground mb-6">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Essential Knowledge</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Legal foundation of affidavits and declarations
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Proper recording procedures and protocols
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Understanding jurisdiction and authority
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">Practical Skills</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Creating legally binding declarations
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Notarization and witnessing requirements
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Public recording and documentation
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground underline">
              Start below
            </h2>
          </div>

          {/* Step Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Step 1 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">1</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Understanding Declarations, Affidavits and Recordings
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 1
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 1: Understanding Declarations, Affidavits and Recordings
                  </DialogTitle>
                </DialogHeader>
                <div className="text-muted-foreground space-y-8 md:space-y-12 mt-4 md:mt-6 text-sm md:text-base">
                  <div>
                    <h4 className="font-bold text-foreground text-lg md:text-xl mb-4 md:mb-6">1. What is a declaration and why it's important to freedom and what it means for you from here on out?</h4>
                    <div className="space-y-4">
                      <p>
                        A declaration is a formal, conscious, and voluntary assertion of truth, belief, status, or intent made by a living man or woman. It is not a request for recognition—it is a proclamation of one's standing, one's purpose, and one's relationship to law, jurisdiction, and authority.
                      </p>
                      <p>
                        In the context of sovereignty and freedom, a declaration is the first act of reclaiming dominion over your own life. It is where you stop being property of the system and begin operating as a principal—not a subject.
                      </p>
                      <p>
                        Whether it's a <strong>Declaration of Status</strong>, <strong>Declaration of Independence</strong>, or <strong>Declaration of Truth</strong>, this instrument:
                      </p>
                      <ul className="list-disc pl-4 md:pl-6 space-y-2 md:space-y-3">
                        <li><strong>Affirms your authority</strong> as the original source of power—not a derivative of the state.</li>
                        <li><strong>Reorients jurisdiction</strong>—it removes presumptions of citizenship, dependency, and subordination.</li>
                        <li><strong>Sets the record straight</strong>—for courts, agencies, and anyone who assumes you're a corporate fiction.</li>
                      </ul>
                      <p>
                        From here on out, a declaration means you move differently. You are no longer operating under presumed statuses or silence. <strong>Silence is consent in law.</strong> But your declaration speaks with authority. It says: I know who I am, I know what governs me, and I do not consent to false constructs.
                      </p>
                      <p className="font-semibold text-foreground">
                        A declaration is not merely a document—it is a weapon of peace, a notice of standing, and the breath of sovereignty in written form.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground text-lg md:text-xl mb-4 md:mb-6">2. What is an affidavit?</h4>
                    <div className="space-y-4">
                      <p>
                        An affidavit is a written testimony of truth given under oath, by a living man or woman, based on firsthand knowledge. It is the highest form of evidence in law, outranking attorney opinion, hearsay, and even judge presumptions—because it is sworn truth.
                      </p>
                      <p>
                        In the world of sovereignty, an affidavit is your shield and your sword. Why?
                      </p>
                      <p>Because:</p>
                      <ul className="list-disc pl-4 md:pl-6 space-y-2 md:space-y-3">
                        <li><strong>It is self-authenticating:</strong> No third-party needed.</li>
                        <li><strong>It stands as fact</strong> unless rebutted by another affidavit of equal or greater weight.</li>
                        <li><strong>It creates the record</strong> upon which all lawful process must rest.</li>
                      </ul>
                      <p>
                        An affidavit says: I was there. I saw it. I experienced it. And I am willing to swear on penalty of perjury that this is true. That alone is unchallenged power if left unrebutted.
                      </p>
                      <p>
                        Affidavits are how sovereign men and women speak in court without begging for permission. You don't need a bar card to file one. You don't need to argue statutes. You state facts, declare harm, assert rights, and let the truth pierce through all fiction.
                      </p>
                      <p>
                        Used correctly, an affidavit is how you hold government agents accountable, rebut false assumptions, establish claims, and bring the truth into the record—where it must be addressed.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground text-lg md:text-xl mb-4 md:mb-6">3. What are recordings and why are they important?</h4>
                    <div className="space-y-4">
                      <p>
                        Recordings are the public memorialization of private truth. They are how you plant your flag in law, saying: This is real. This happened. And I have declared it before the world.
                      </p>
                      <p>
                        In the realm of sovereignty and authority, recording is what separates the man who talks from the man who walks. It is how you enter your truth into the legal and historical record—not in private, not in secret, but openly and officially.
                      </p>
                      <p>Recording:</p>
                      <ul className="list-disc pl-4 md:pl-6 space-y-2 md:space-y-3">
                        <li>Makes your declarations and affidavits undeniable.</li>
                        <li>Secures your standing in the public domain.</li>
                        <li>Establishes constructive notice to all actors—government, corporations, and private parties.</li>
                        <li>Creates evidence that survives you.</li>
                      </ul>
                      <p>
                        <strong>If it's not recorded, it didn't happen.</strong> That's how the system sees it.
                      </p>
                      <p>
                        You can declare all day. You can write the most powerful affidavit ever penned. But until it's recorded, you are a whisper in the wind. Recording is how the sovereign makes his word flesh in the world of paper.
                      </p>
                      <p>
                        From here on out, recording means you're no longer hiding in shadows. You're standing boldly in the light of law, as one who has spoken, has recorded, and will not be moved.
                      </p>
                      
                      <div className="border-l-4 border-primary pl-4 md:pl-6 bg-primary/5 p-4 md:p-6 rounded-r mt-6 md:mt-8">
                        <p className="font-bold text-foreground mb-3 text-base md:text-lg">Let this be clear:</p>
                        <div className="space-y-2">
                          <p><strong>A declaration</strong> asserts your status.</p>
                          <p><strong>An affidavit</strong> proves your truth.</p>
                          <p><strong>A recording</strong> immortalizes both.</p>
                        </div>
                        <p className="font-semibold text-foreground mt-4">
                          Together, they are the trinity of lawful power for the sovereign man or woman. When used with integrity, they do not just challenge the system—they transcend it.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6">
                    <h4 className="font-bold text-foreground text-base md:text-lg mb-3 md:mb-4">⏰ Time for Action</h4>
                    <p className="text-muted-foreground">
                      Explain to yourself what a declaration is, what an affidavit is, and what a recording is and why they are important.
                    </p>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep2Open(true)}
                      className="px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Go to Step 2
                    </Button>
                  </div>
                  
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 2 */}
            <Dialog open={step2Open} onOpenChange={setStep2Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">2</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Making Your First Declaration
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 2
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 2: Making Your First Declaration
                  </DialogTitle>
                </DialogHeader>
                <div className="text-muted-foreground space-y-8 md:space-y-12 mt-4 md:mt-6 text-sm md:text-base">
                  <div>
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
                      <h4 className="font-bold text-foreground text-lg md:text-xl mb-3 md:mb-4">Let's begin with an Example of a Declaration</h4>
                      <div className="space-y-2 md:space-y-3 italic text-foreground text-sm md:text-base">
                        <p>"I am a living [man/woman] of the land.</p>
                        <p>I am sovereign, free, and under God's law.</p>
                        <p>I stand under common law, not under statutes made for corporate persons.</p>
                        <p>I do not consent to being a 14th Amendment U.S. citizen, nor to any presumption of subject-class status.</p>
                        <p>I was born free. I remain free. My rights are inherent—not granted."</p>
                      </div>
                    </div>

                    <div className="space-y-4 md:space-y-6">
                      <p>
                        <strong>This is a declaration, not an affidavit.</strong> That distinction matters.
                      </p>
                      
                      <div className="space-y-4">
                        <p>
                          <strong>An affidavit</strong> is a statement of fact sworn under penalty of perjury—used in legal processes.
                        </p>
                        <p>
                          But a declaration like this? <strong>It's not for them. It's for you.</strong> It is spiritual, foundational, and personal.
                        </p>
                        <p>
                          It is a statement of your intent, your truth, and your standing as a living soul—not a corporate fiction.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <p>
                          Think about this for a second, the <strong>Declaration of Independence</strong>—the very foundation of what later became the United States of America Constitutional Republic—was just that... a declaration.
                        </p>
                        <ul className="list-disc pl-4 md:pl-6 space-y-2">
                          <li>It was not notarized.</li>
                          <li>It was not presented in court.</li>
                          <li>It wasn't approved by any authority.</li>
                        </ul>
                        <p>
                          It was simply <strong>spoken into being</strong> by those who knew their rights came from their Creator—not from kings or governments.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <p>
                          That document changed the world. And it did so because men spoke their truth boldly, knowing that when a sovereign declares—they invoke a law higher than and of man's law.
                        </p>
                        <p>
                          So too, when you make bold declarations, it is not about paperwork—it is about <strong>the jurisdiction of your very existence.</strong>
                        </p>
                      </div>

                      <div className="space-y-4">
                        <p>
                          When you know who you are, the system loses its grip.
                        </p>
                        <p>
                          Your declarations are your first step to stepping fully into your rightful position—as one who is self governed under Divine law.
                        </p>
                      </div>

                      <div className="border-l-4 border-primary pl-4 md:pl-6 bg-primary/5 p-4 md:p-6 rounded-r mt-6 md:mt-8">
                        <p className="font-bold text-foreground mb-3 md:mb-4 text-base md:text-lg">Make it real. Speak it out loud. Say it Proud.</p>
                        <p>
                          And when you're ready, record it into the world's system—not to ask for recognition, but to put them on notice.
                        </p>
                        <p className="mt-4">
                          <strong>You are no longer lost in their fiction.</strong>
                        </p>
                        <p className="font-semibold text-foreground">
                          <strong>You have declared yourself found.</strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6">
                    <h4 className="font-bold text-foreground text-base md:text-lg mb-3 md:mb-4">⏰ Time for Action</h4>
                    <p className="text-muted-foreground">
                      Make your first declaration. Take as long as you need and once you finished move on to step three.
                    </p>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep3Open(true)}
                      className="px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Go to Step 3
                    </Button>
                  </div>
                  
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 3 */}
            <Dialog open={step3Open} onOpenChange={setStep3Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">3</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Create Your First Affidavit
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 3
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Turn Your Inner Truth Into Legal Power: Create Your First Affidavit
                  </DialogTitle>
                </DialogHeader>
                <div className="text-muted-foreground space-y-8 md:space-y-12 mt-4 md:mt-6 text-sm md:text-base">
                  <div>
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
                      <h4 className="font-bold text-foreground text-lg md:text-xl mb-3 md:mb-4">Intro to the Step:</h4>
                      <div className="space-y-3 md:space-y-4 text-sm md:text-base leading-relaxed">
                        <p>You've just declared who you are in the previous section. That was spiritual. That was inner knowing. That was your proclamation of truth.</p>
                        <p>Now it's time to anchor that truth in law—by creating your first affidavit.</p>
                        <p>Where a declaration expresses who you are in spirit, an affidavit memorializes that truth as lawful evidence. It moves from the realm of belief into the realm of fact—unchallenged unless rebutted by another affidavit of equal standing.</p>
                        <p>You're not asking for permission. You're putting the world on notice, and you're doing it under the highest lawful standard: firsthand knowledge, sworn truth, and accountability under perjury.</p>
                      </div>
                    </div>

                    <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4 md:p-6">
                      <h4 className="font-bold text-foreground text-lg md:text-xl mb-3 md:mb-4">Example Affidavit Based on Your Declaration:</h4>
                      <h5 className="font-bold text-foreground text-base md:text-lg mb-3 md:mb-4 text-center">Affidavit of Status</h5>
                      <div className="space-y-2 md:space-y-3 text-foreground text-sm md:text-base">
                        <p>I, [Your Full Name], a living [man/woman] born of the land, competent to testify and having firsthand knowledge of the facts herein, do solemnly affirm the following to be true, correct, and complete to the best of my knowledge, belief, and ability:</p>
                        <p className="pl-4">I am a sovereign [man/woman], free by birthright, under the authority of the Creator and governed by Natural Law and the principles of Common Law.</p>
                        <p className="pl-4">I am not a 14th Amendment U.S. citizen, nor do I consent to being treated as a corporate entity, vessel, fiction, or subject of any foreign jurisdiction, including but not limited to federal, municipal, or private administrative constructs.</p>
                        <p className="pl-4">I do not operate under any compelled benefits, privileges, or contracts I did not knowingly and willingly enter into with full disclosure.</p>
                        <p className="pl-4">My rights are inherent, unalienable, and do not come from government, constitutions, or contracts—they are endowed by the Creator.</p>
                        <p className="pl-4">Any and all attempts to presume jurisdiction over me without my explicit consent constitute fraud, trespass, and a violation of Natural Law.</p>
                        <p className="pl-4">I make this affidavit freely, without coercion, under the penalty of perjury, and I reserve all rights, including the right to amend and correct this record at any time as necessary.</p>
                        <p>Executed on this ____ day of __________, [Year], on the land commonly known as [Your County], [Your State Republic].</p>
                      </div>
                    </div>

                    <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 md:p-6">
                      <div className="space-y-3 md:space-y-4 text-sm md:text-base leading-relaxed">
                        <p className="font-semibold">This affidavit is your armor in the system.</p>
                        <p>Unlike a declaration, which speaks to your inner truth, the affidavit speaks to the outer system—on your terms, under your authority.</p>
                        <p>Once this is written, you must now have it notarized, as this stands as your oath and truth before all.</p>
                        <p className="font-semibold italic">This is how you move with both spirit and law in alignment.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6">
                    <h4 className="font-bold text-foreground text-base md:text-lg mb-3 md:mb-4">⏰ Time for Action</h4>
                    <p className="text-muted-foreground">
                      Create your first affidavit using the template above. Have it notarized and prepare it for recording.
                    </p>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep4Open(true)}
                      className="px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Go to Step 4
                    </Button>
                  </div>
                  
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 4 */}
            <Dialog open={step4Open} onOpenChange={setStep4Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">4</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Record Your First Affidavit Publicly
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 4
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 4: Record Your First Affidavit Publicly
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
                      <h4 className="font-bold text-foreground text-lg md:text-xl mb-3 md:mb-4">Words of Instruction and Power:</h4>
                      <div className="space-y-3 md:space-y-4 text-sm md:text-base leading-relaxed">
                        <p>Now that you've declared your truth and memorialized it in an affidavit, the next sacred step is this: <strong>Put the world on notice.</strong></p>
                        
                        <div className="space-y-2">
                          <p className="font-semibold">Why?</p>
                          <p>Because in law, that which is not rebutted stands as truth.</p>
                          <p>But that which is not noticed, can be ignored.</p>
                        </div>

                        <p>A man or woman of sovereignty does not whisper their truth in secret. They broadcast it with lawful boldness.</p>
                        
                        <div className="space-y-2">
                          <p>You are not hiding from the system—you are correcting it.</p>
                          <p>You are not begging recognition—you are claiming your rightful jurisdiction.</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4 md:p-6">
                      <h4 className="font-bold text-foreground text-lg md:text-xl mb-3 md:mb-4">How to Give Public Notice:</h4>
                      
                      <div className="space-y-6">
                        <div>
                          <h5 className="font-semibold text-foreground mb-3">Go to Any County Clerk's Office in the United States</h5>
                          <div className="space-y-2 text-sm md:text-base leading-relaxed">
                            <p>Walk in—not as a subject, but as one standing in truth.</p>
                            <p>Present your affidavit and ask that it be recorded into the public record.</p>
                            <p>You're not asking for approval. You're establishing lawful evidence and making it available for all who need to see it—courts, agencies, or anyone who would presume to act upon your status.</p>
                            <p>If one county gives you trouble, go to another.</p>
                            <p><em>Keep your composure—a sovereign is always calm.</em></p>
                            <p>Your truth does not require permission to exist. Only proper record to be acknowledged.</p>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-semibold text-foreground mb-3">Create a Public Website or Publish in a Public Domain</h5>
                          <div className="space-y-2 text-sm md:text-base leading-relaxed">
                            <p>If the county clerk refuses or limits access, don't stop.</p>
                            <p>Create your own public notice center—a simple website where your declaration and affidavit are publicly visible and time-stamped.</p>
                            <p>This becomes your digital courthouse—lawfully sufficient notice to the world.</p>
                          </div>
                        </div>

                        <div>
                          <p className="font-semibold mb-2">You may also:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm md:text-base leading-relaxed ml-4">
                            <li>Use blockchain-based recorders for immutable time stamps.</li>
                            <li>Publish to notice platforms, bulletin boards, or law journals.</li>
                            <li>Send copies to agencies, courts, or parties who need to be noticed.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 md:p-6">
                      <h4 className="font-bold text-foreground text-lg md:text-xl mb-3 md:mb-4">Why This Matters:</h4>
                      <div className="space-y-3 md:space-y-4 text-sm md:text-base leading-relaxed">
                        <p>Public notice is more than a technical step.</p>
                        <p>It's your lawful footprint in the system's domain. It tells them:</p>
                        
                        <blockquote className="border-l-4 border-primary pl-4 italic font-medium">
                          "I am not lost. I am not a corporate fiction. I am alive, standing, and I have corrected the record."
                        </blockquote>

                        <p>And in the world of law—the record is everything.</p>
                        
                        <div className="space-y-2">
                          <p>No court, no agency, no actor can now say they "didn't know."</p>
                          <p>They were noticed.</p>
                          <p>And by law, if they do not rebut your sworn affidavit with their own—your truth stands as final judgment.</p>
                        </div>

                        <p>This is how men and women of God's law make their presence known in the legal matrix—not by rebellion, but by righteous correction.</p>
                        
                        <p className="font-semibold text-primary">So go now, make your notice. Record your truth.</p>
                        <p className="font-semibold italic">Let the world know: You have returned to yourself—and the law now bends to that truth.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6 mt-6">
                    <h4 className="font-bold text-foreground text-base md:text-lg mb-3 md:mb-4">⏰ Time for Action</h4>
                    <p className="text-muted-foreground">
                      Record your affidavit publicly through one of the methods described above. Make your truth known to the world.
                    </p>
                  </div>

                  <div className="mt-6 text-center">
                    <Link to="/diy-processes">
                      <Button 
                        className="px-8 py-3 bg-green-600 text-white hover:bg-green-700"
                      >
                        Complete
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>
          
            {/* Step 5 */}
            <Link to="/category/affidavits/videos">
              <div className="group cursor-pointer">
                <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                      <span className="text-xl font-bold text-primary">5</span>
                    </div>
                    <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                      More resources to consider in understanding affidavits
                    </h3>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                    Open Resources
                  </Button>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AffidavitsDeclarations;